import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Edit2, Trash2, Save, X, Plus, Package, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EditProductModal({ product, onClose, onSaved }) {
  const isNew = !product?.id;
  const [data, setData] = useState({
    code: product?.code || "",
    description: product?.description || "",
    technical_specs: product?.technical_specs || "",
    unit: product?.unit || "PZA",
    last_price: product?.last_price ?? 0,
    last_supplier: product?.last_supplier || "",
    notes: product?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!data.description.trim() && !data.code.trim()) {
      toast.error("Indica al menos código o descripción");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/comercial-products`, data);
        toast.success("Producto creado");
      } else {
        await axios.put(`${API}/comercial-products/${product.id}`, data);
        toast.success("Producto actualizado");
      }
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Error al guardar producto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="edit-product-modal">
      <div className="w-full max-w-2xl bg-[#0f1a2b] border border-slate-700 rounded-xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-500" />
            {isNew ? "Nuevo Producto" : "Editar Producto"}
          </h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Código / Clave</label>
              <input
                type="text"
                value={data.code}
                onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })}
                placeholder="Ej: PROD001"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono uppercase focus:border-emerald-500 focus:outline-none"
                data-testid="product-code-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Unidad</label>
              <input
                type="text"
                value={data.unit}
                onChange={(e) => setData({ ...data, unit: e.target.value.toUpperCase() })}
                placeholder="PZA, TON, KG..."
                list="unit-options"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                data-testid="product-unit-input"
              />
              <datalist id="unit-options">
                <option value="PZA" /><option value="TONELADA" /><option value="KG" />
                <option value="LITRO" /><option value="GALÓN" /><option value="METRO" />
                <option value="M2" /><option value="M3" /><option value="CAJA" />
                <option value="PAR" /><option value="JUEGO" /><option value="ROLLO" />
                <option value="BULTO" /><option value="LOTE" /><option value="SERVICIO" />
              </datalist>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Descripción *</label>
            <input
              type="text"
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              placeholder="Nombre/concepto del producto"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
              data-testid="product-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Último Precio (sin IVA)</label>
              <input
                type="number"
                step="0.01"
                value={data.last_price}
                onChange={(e) => setData({ ...data, last_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono focus:border-emerald-500 focus:outline-none"
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Último Proveedor</label>
              <input
                type="text"
                value={data.last_supplier}
                onChange={(e) => setData({ ...data, last_supplier: e.target.value })}
                placeholder="Nombre del proveedor"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Ficha técnica / Especificaciones</label>
            <textarea
              value={data.technical_specs}
              onChange={(e) => setData({ ...data, technical_specs: e.target.value })}
              rows={5}
              placeholder="Especificaciones, características, normativas..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none resize-y"
              data-testid="product-specs-input"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Notas</label>
            <input
              type="text"
              value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
              placeholder="Notas internas"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
            data-testid="product-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Crear" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComercialProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/comercial-products`);
      setProducts(r.data?.products || []);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return products;
    return products.filter(p =>
      (p.code || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q) ||
      (p.last_supplier || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto del catálogo?")) return;
    try {
      await axios.delete(`${API}/comercial-products/${id}`);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="comercial-products-tab">
      {editing && (
        <EditProductModal
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={fetchProducts}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <Package className="w-7 h-7 text-emerald-500" />
          Catálogo de Productos — Comercializadora
          <span className="text-sm font-normal text-slate-400 ml-2">
            ({products.length} productos)
          </span>
        </h2>
        <div className="flex gap-2">
          <button
            onClick={fetchProducts}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white"
            title="Recargar"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setEditing("new")}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            data-testid="new-product-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Producto
          </button>
        </div>
      </div>

      <div className="glass-card rounded-lg p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código, descripción o proveedor..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            data-testid="search-products-input"
          />
        </div>
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-slate-400">Cargando productos...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">
              {products.length === 0
                ? "No hay productos en el catálogo. Crea uno o se llenará automáticamente al guardar cotizaciones."
                : "Sin resultados para la búsqueda"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Código</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Descripción</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Unidad</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-slate-400">Último Precio (s/IVA)</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Veces usado</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Proveedor</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      {p.code ? (
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded font-mono text-sm font-semibold">{p.code}</span>
                      ) : (
                        <span className="text-slate-600 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white">{p.description || "-"}</td>
                    <td className="px-4 py-3 text-slate-300 text-sm">{p.unit}</td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-mono">{fmt(p.last_price)}</td>
                    <td className="px-4 py-3 text-center text-slate-400">{p.usage_count || 0}</td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{p.last_supplier || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setEditing(p)}
                          className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-slate-700 rounded"
                          title="Editar"
                          data-testid={`edit-product-btn-${p.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                          title="Eliminar"
                          data-testid={`delete-product-btn-${p.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
